import { CSSProperties, useEffect, useState } from "react";
import { calculateThemeColors, clsx, generateShortUniqueId, makeKey, styx } from "modules/Utils";
import WebsiteBuilderPhonePreview from "components/websiteBuilder/WebsiteBuilderPhonePreview";
import ButtonSymphony from "elements/ButtonSymphony";
import Icon from "elements/Icon";
import ModalSymphony from "elements/ModalSymphony"
import RecordSearchView from "elements/RecordSearchView";
import TagSymphony, { TagColors } from "elements/TagSymphony";
import useCurrentBrand, { BrandExistsState } from "Hooks/useCurrentBrand";
import useScreen from "Hooks/useScreen";
import useWebsiteBuilder, { getWebsiteBuilderPath } from "Hooks/useWebsiteBuilder"
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import { RecordDetails } from "modules/Types";
import { useHistory } from "react-router-dom";
import DebugBox from "./DebugBox";
import { getPalette } from 'react-palette';
import { WebsiteBuilderTemplateType, WEBSITE_TEMPLATE_INFO, WebsiteTemplateCategory, WEBSITE_TEMPLATE_CATEGORIES } from "modules/Const";
import WebsiteGallery from "components/WebsiteGallery";

const WEBSITE_PREVIEW_ZOOM = 0.6;

interface CreateNewWebsiteModalV2Props {
    className?: string;
    isModal?: boolean;
    isRedirect?: boolean;
}

const CreateNewWebsiteModalV2 = ({
    className,
    isModal = true,
    isRedirect = false,
}: CreateNewWebsiteModalV2Props) => {
    const history = useHistory();
    const {
        cssCalc,
        mobileView,
    } = useScreen();
    const {
        currentBrand,
        getBrandImage,
    } = useCurrentBrand() as BrandExistsState;
    const {
        showCreateNewWebsiteModalV2,
        setShowCreateNewWebsiteModalV2,
        getTemplate,
        setWebsiteContent,
        setWebsiteStyle,
        resetWebsiteBuilder,
        setInitialRecord,
        initialRecord,
        selectedTemplateCategory,
        setSelectedTemplateCategory,
        isLoadingWebsiteBuilder,
    } = useWebsiteBuilder()

    const getInitialWebsiteStyleColors = async () => {
        const image = getBrandImage();
        const paletteData = await getPalette(image);
        const {
            backgroundColor,
            secondaryColor,
            textColor
        } = calculateThemeColors(paletteData);

        setWebsiteStyleColors({
            backgroundColor,
            secondaryColor,
            textColor,
        });
    }

    const getInitialWebsiteStyleMusicColors = async (recordDetails: RecordDetails | null) => {
        if (!recordDetails) {
            return;
        }

        const image = recordDetails.thumbnail_url;
        const paletteData = await getPalette(image);
        const {
            backgroundColor,
            secondaryColor,
            textColor
        } = calculateThemeColors(paletteData);

        setWebsiteStyleMusicColors({
            backgroundColor,
            secondaryColor,
            textColor,
        });

        setShowSelectMusicModal(false)
    }

    const websiteSlug = generateShortUniqueId()
    const websiteBuilderPath = getWebsiteBuilderPath(currentBrand.slug, websiteSlug)

    const [showSelectMusicModal, setShowSelectMusicModal] = useState<boolean>(false)
    const [websiteStyleColors, setWebsiteStyleColors] = useState<{
        backgroundColor: string;
        secondaryColor: string;
        textColor: string;
    } | null>(null)
    const [websiteStyleMusicColors, setWebsiteStyleMusicColors] = useState<{
        backgroundColor: string;
        secondaryColor: string;
        textColor: string;
    } | null>(null) // NOTE: right now just the colors

    const initializeWebsiteTemplates = async () => {
        console.log('initializeWebsiteTemplates >>>>', initialRecord)
        await getInitialWebsiteStyleColors();
        await getInitialWebsiteStyleMusicColors(initialRecord);
    }

    useEffect(() => {
        // NOTE: we need this componentDidMount for isModal == false
        initializeWebsiteTemplates();
    }, [initialRecord])

    useEffect(() => {
        // NOTE: this is simulating componentDidMount for modals
        if (!showCreateNewWebsiteModalV2) {
            return;
        }

        initializeWebsiteTemplates();
    }, [showCreateNewWebsiteModalV2])

const selectWebsiteTemplate = (websiteTemplateType: WebsiteBuilderTemplateType) => {
    const websiteTemplate = getTemplate({
        templateType: websiteTemplateType,
        // selectedMusic: selectedMusic,
    })
    if (!websiteTemplate) {
        return
    }

    if (websiteTemplateType === WebsiteBuilderTemplateType.BLANK) {
        resetWebsiteBuilder();
    }

    const {
        websiteContent,
        websiteStyle,
    } = websiteTemplate

    setWebsiteContent(websiteContent)
    const websiteTemplateCategory = WEBSITE_TEMPLATE_INFO[websiteTemplateType].websiteTemplateCategory
    if (websiteTemplateCategory === WebsiteTemplateCategory.MUSIC && websiteStyleMusicColors) {
        setWebsiteStyle({
            ...websiteStyle,
            ...websiteStyleMusicColors,
        })
    } else {
        setWebsiteStyle({
            ...websiteStyle,
            ...websiteStyleColors,
        })
    }

    setShowCreateNewWebsiteModalV2(false)
    if (isRedirect) {
        history.push(websiteBuilderPath)
    }
}

const handleSelectRecord = async (recordDetails: RecordDetails) => {
    setInitialRecord(recordDetails)
    await getInitialWebsiteStyleMusicColors(recordDetails);
}

const renderSelectMusicButton = () => {
    return (
        <ButtonSymphony
            className="createNewWebsiteModalV2MusicButton"
            style={{
                position: 'absolute',
                right: 0,
                height: 30,
                marginRight: 16,
            }}
            size="small"
            onClick={() => {
                setShowSelectMusicModal(true)
            }}
        >
            Select Music
        </ButtonSymphony>
    )
}

const renderWebsiteTemplateCategories = () => {
    return (
        <div
            className="websiteTemplateTypesContainer"
            style={sty.websiteTemplateTypesContainer}
        >
            {WEBSITE_TEMPLATE_CATEGORIES.map((template, index) => {
                const {
                    name,
                    title,
                    icon,
                    color,
                    iconColor,
                } = template;

                const isSelected = selectedTemplateCategory === name

                return (
                    <TagSymphony
                        key={makeKey('websiteTemplate', name, index)}
                        className="templateMenuListItem"
                        onClick={() => setSelectedTemplateCategory(name)}
                        title={title}
                        icon={icon}
                        color={isSelected ? color : TagColors.grey}
                        iconColor={isSelected ? iconColor : undefined}
                    />
                )
            })}
            {selectedTemplateCategory === WebsiteTemplateCategory.MUSIC &&
                renderSelectMusicButton()
            }
        </div>
    )
}

const renderTemplates = () => {
    if (selectedTemplateCategory === WebsiteTemplateCategory.GALLERY) {
        return null
    }

    return (
        <div className="websiteTemplatePreviewContainer" style={sty.websiteTemplatePreviewContainer}>
            {Object.entries(WEBSITE_TEMPLATE_INFO)
                .filter(([templateType]) => {
                    const websiteTemplateType = templateType as WebsiteBuilderTemplateType;
                    const websiteTemplateInfo = WEBSITE_TEMPLATE_INFO[websiteTemplateType]

                    if (websiteTemplateInfo.isDisabled) {
                        return false
                    }

                    if (selectedTemplateCategory === WebsiteTemplateCategory.ALL_TEMPLATES) {
                        return true
                    }

                    if (selectedTemplateCategory === WebsiteTemplateCategory.POPULAR) {
                        return websiteTemplateInfo.isPopular
                    }

                    const templateCategory = websiteTemplateInfo.websiteTemplateCategory
                    switch (selectedTemplateCategory) {
                        case WebsiteTemplateCategory.MUSIC:
                            return templateCategory === WebsiteTemplateCategory.MUSIC
                        case WebsiteTemplateCategory.EVENTS:
                            return templateCategory === WebsiteTemplateCategory.EVENTS
                        case WebsiteTemplateCategory.COLLECT_DATA:
                            return templateCategory === WebsiteTemplateCategory.COLLECT_DATA
                        case WebsiteTemplateCategory.POPULAR:
                            return templateCategory === WebsiteTemplateCategory.POPULAR
                        default:
                            return false
                    }
                })
                .map(([templateType], index) => {
                    const websiteTemplateType = templateType as WebsiteBuilderTemplateType;
                    const websiteTemplate = getTemplate({
                        templateType: websiteTemplateType,
                    })

                    if (!websiteTemplate) {
                        return null
                    }
                    const websiteTemplateInfo = WEBSITE_TEMPLATE_INFO[websiteTemplateType]
                    const {
                        websiteTemplateCategory,
                        title,
                        isFree,
                    } = websiteTemplateInfo

                    const {
                        websiteContent,
                        websiteStyle: websiteStyleFromTemplate,
                    } = websiteTemplate

                    // if it's NOT a music template, use the website style colors
                    let websiteStyleLocal = {
                        ...websiteStyleFromTemplate,
                        ...websiteStyleColors,
                    }

                    const templateIsMusic = websiteTemplateCategory === WebsiteTemplateCategory.MUSIC

                    // if it's a music template, use the music colors
                    if (templateIsMusic && websiteStyleMusicColors) {
                        websiteStyleLocal = {
                            ...websiteStyleFromTemplate,
                            ...websiteStyleMusicColors,
                        }
                    }

                    return (
                        <div
                            key={makeKey('websiteTemplate', websiteTemplateType, index)}
                            className="websiteTemplatePreview"
                            style={sty.websiteTemplatePreview}
                        >
                            <div
                                className="websiteTemplatePreviewTitle"
                                style={{
                                    ...Sty.text16bold,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <div className="websiteTemplatePreviewTitleText">{title}</div>
                                {!isFree &&
                                    <Icon
                                        className="websiteTemplatePreviewTitleIcon"
                                        style={{
                                            marginBottom: 5,
                                        }}
                                        icon="gem"
                                        size={16}
                                    />
                                }
                            </div>
                            <WebsiteBuilderPhonePreview
                                className="websiteTemplatePreviewPhonePreviewFromCreateNewWebsiteModalV2"
                                style={{ zoom: WEBSITE_PREVIEW_ZOOM }}
                                websiteContent={websiteContent}
                                websiteStyle={websiteStyleLocal}
                                isSticky={false}
                                isFooter={false}
                                isLocalTabLogic
                            />
                            <ButtonSymphony
                                className="websiteTemplatePreviewButton"
                                onClick={() => selectWebsiteTemplate(websiteTemplateType)}
                                size="small"
                            >
                                Remix Template
                            </ButtonSymphony>
                        </div>
                    )
                })}
        </div>
    )
}

const renderGalleryTemplates = () => {
    if (selectedTemplateCategory !== WebsiteTemplateCategory.GALLERY) {
        return null
    }

    return (
        <WebsiteGallery
            className="websiteGalleryTemplates"
            isCard={false}
            zoom={WEBSITE_PREVIEW_ZOOM}
            isSmallButton
            isRedirect={isRedirect}
        />
    )
}

const renderCreateNewWebsiteV2 = () => {
    return (
        <>
            <div className="websiteTemplateCategoriesContainer" style={sty.websiteTemplateCategoriesContainer}>
                {renderWebsiteTemplateCategories()}
            </div>
            <div
                className="websiteTemplateModalBody"
                style={styx(
                    sty.websiteTemplateModalBody,
                    isModal ?
                        { height: cssCalc(252) }
                        :
                        { height: 'auto', borderRadius: 16 }
                )}
            >
                {renderTemplates()}
                {renderGalleryTemplates()}
            </div>
        </>
    )
}

return (
    <>
        {isModal ?
            <ModalSymphony
                className={clsx('createNewWebsiteModalV2Component', className)}
                isOpen={showCreateNewWebsiteModalV2}
                onClose={() => setShowCreateNewWebsiteModalV2(false)}
                title="Start with a template"
                subtitle="Choose from a variety of templates to get started."
                isIsolateModalContent
                size="large"
            >
                {renderCreateNewWebsiteV2()}
            </ModalSymphony>
            :
            renderCreateNewWebsiteV2()
        }

        <ModalSymphony
            className="createNewWebsiteModalV2SelectMusicModal"
            isOpen={showSelectMusicModal}
            onClose={() => setShowSelectMusicModal(false)}
            title="Select Music"
            subtitle="Choose a song to get started."
        >
            <RecordSearchView
                letUserSearchSpotify
                handleSelectRecord={handleSelectRecord}
                options={['single', 'project', 'playlist']}
                maxHeight={mobileView ? cssCalc(410) : cssCalc(330)}
            />
        </ModalSymphony>
    </>
)
}

const sty: Record<string, CSSProperties> = {
    createNewWebsiteModalV2LeftSideContent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 16,
        padding: 16,
    },
    websiteTemplatePreviewContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 24,
        width: '100%',
        cursor: 'pointer',
    },
    websiteTemplatePreview: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        backgroundColor: Colors.greyLighter,
        borderRadius: 16,
    },

    websiteTemplateTypesContainer: {
        display: 'flex',
        position: 'relative',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 8,
    },

    websiteTemplateCategoriesContainer: {
        paddingBottom: 16,
    },

    websiteTemplateModalBody: {
        backgroundColor: Colors.greyLighter,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'scroll',
        padding: 16,
        boxShadow: Sty.boxShadowInset,
    },

}

export default CreateNewWebsiteModalV2;